import React, { useState, useEffect } from 'react';
import { Edit2, X, TrendingUp, TrendingDown, DollarSign, RefreshCw, AlertTriangle } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';
import EditPositionModal from '../modals/EditPositionModal';

const PositionsTab = ({ details, onRefresh, clientId }) => {
  const { toast } = useAdminToast();
  const [editingPosition, setEditingPosition] = useState(null);
  const [closingPosition, setClosingPosition] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [marketPrices, setMarketPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [closePrice, setClosePrice] = useState('');
  const [closePnl, setClosePnl] = useState('');

  // Fetch market prices on mount
  useEffect(() => {
    fetchMarketPrices();
  }, []);

  const fetchMarketPrices = async () => {
    setLoadingPrices(true);
    try {
      const res = await adminAPI.getMarketPrices();
      setMarketPrices(res.data?.prices || {});
    } catch (err) {
      console.error('Failed to fetch prices');
    } finally {
      setLoadingPrices(false);
    }
  };

  // Get current market price for a symbol
  const getMarketPrice = (symbol) => {
    // Symbol like BTCUSDT -> BTC
    const base = symbol?.replace('USDT', '').replace('USD', '');
    return marketPrices[base] || 0;
  };

  // Calculate PnL based on current/close price
  const calculatePnL = (pos, price) => {
    if (!price || !pos.entry_price || !pos.quantity) return 0;
    const entryPrice = parseFloat(pos.entry_price);
    const qty = parseFloat(pos.quantity);
    const closeP = parseFloat(price);
    
    if (pos.side === 'long') {
      return (closeP - entryPrice) * qty;
    } else {
      return (entryPrice - closeP) * qty;
    }
  };

  // Open close modal with current price
  const openCloseModal = (pos) => {
    const currentPrice = getMarketPrice(pos.symbol);
    setClosePrice(currentPrice.toString());
    setClosePnl(calculatePnL(pos, currentPrice).toFixed(2));
    setClosingPosition(pos);
  };

  // Update PnL when close price changes
  const handleClosePriceChange = (value, pos) => {
    setClosePrice(value);
    if (value) {
      setClosePnl(calculatePnL(pos, parseFloat(value)).toFixed(2));
    }
  };

  const closePosition = async (positionId, type, customPrice = null, customPnl = null) => {
    setProcessing(positionId);
    try {
      await adminAPI.closePosition(positionId, type, customPrice, customPnl);
      setClosingPosition(null);
      onRefresh();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setProcessing(null);
    }
  };

  const reopenPosition = async (positionId) => {
    if (!window.confirm('Восстановить закрытую позицию?')) return;
    setProcessing(positionId);
    try {
      await adminAPI.reopenPosition(positionId);
      onRefresh();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setProcessing(null);
    }
  };

  const positions = details?.positions || [];
  const openPositions = positions.filter(p => p.status === 'open');
  const closedPositions = positions.filter(p => p.status === 'closed');

  return (
    <div>
      {/* Header with refresh prices */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
          Открытых: <span style={{ color: COLORS.success }}>{openPositions.length}</span>
          {closedPositions.length > 0 && <span> • Закрытых: {closedPositions.length}</span>}
        </div>
        <button
          onClick={fetchMarketPrices}
          disabled={loadingPrices}
          style={{
            padding: '6px 12px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '6px',
            color: COLORS.textSecondary,
            fontSize: '11px',
            cursor: loadingPrices ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <RefreshCw size={12} className={loadingPrices ? 'animate-spin' : ''} />
          Обновить цены
        </button>
      </div>

      {/* Open Positions */}
      {openPositions.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ color: COLORS.success, fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
            Открытые позиции
          </div>
          {openPositions.map((pos, i) => {
            const currentPrice = getMarketPrice(pos.symbol);
            const currentPnl = calculatePnL(pos, currentPrice);
            const entryPrice = parseFloat(pos.entry_price) || 0;
            const priceChange = currentPrice && entryPrice ? ((currentPrice - entryPrice) / entryPrice * 100) : 0;
            
            return (
              <div key={pos.id || i} style={{
                padding: '14px',
                backgroundColor: COLORS.background,
                borderRadius: '10px',
                marginBottom: '10px',
                border: `1px solid ${pos.side === 'long' ? COLORS.success : COLORS.danger}30`,
              }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {pos.side === 'long' ? <TrendingUp size={18} color={COLORS.success} /> : <TrendingDown size={18} color={COLORS.danger} />}
                    <span style={{ color: COLORS.text, fontWeight: '700', fontSize: '15px' }}>{pos.symbol}</span>
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: pos.side === 'long' ? `${COLORS.success}20` : `${COLORS.danger}20`,
                      color: pos.side === 'long' ? COLORS.success : COLORS.danger,
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>
                      {pos.side?.toUpperCase()} {pos.leverage}x
                    </span>
                  </div>
                  <button
                    onClick={() => setEditingPosition(pos)}
                    disabled={processing === pos.id}
                    data-testid="edit-position"
                    style={{
                      padding: '6px 10px',
                      backgroundColor: `${COLORS.primary}15`,
                      border: 'none',
                      borderRadius: '6px',
                      color: COLORS.primary,
                      fontSize: '11px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Edit2 size={12} /> Изменить
                  </button>
                </div>

                {/* Position Data */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Вход</div>
                    <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>${entryPrice.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Текущая</div>
                    <div style={{ color: COLORS.primary, fontSize: '13px', fontWeight: '600' }}>
                      ${currentPrice.toFixed(2)}
                      {priceChange !== 0 && (
                        <span style={{ 
                          fontSize: '10px', 
                          marginLeft: '4px',
                          color: priceChange >= 0 ? COLORS.success : COLORS.danger 
                        }}>
                          ({priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%)
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Объём</div>
                    <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>{pos.quantity}</div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>Маржа</div>
                    <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>
                      ${((entryPrice * parseFloat(pos.quantity || 0)) / (pos.leverage || 1)).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>PnL (текущий)</div>
                    <div style={{ 
                      color: currentPnl >= 0 ? COLORS.success : COLORS.danger, 
                      fontSize: '14px', 
                      fontWeight: '700' 
                    }}>
                      {currentPnl >= 0 ? '+' : ''}${currentPnl.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* SL/TP */}
                {(pos.stop_loss || pos.take_profit) && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    padding: '8px 10px', 
                    backgroundColor: COLORS.card, 
                    borderRadius: '6px',
                    marginBottom: '10px',
                    fontSize: '12px',
                  }}>
                    <div>
                      <span style={{ color: COLORS.textSecondary }}>Stop Loss: </span>
                      <span style={{ color: COLORS.danger, fontWeight: '600' }}>
                        {pos.stop_loss ? `$${pos.stop_loss}` : '—'}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: COLORS.textSecondary }}>Take Profit: </span>
                      <span style={{ color: COLORS.success, fontWeight: '600' }}>
                        {pos.take_profit ? `$${pos.take_profit}` : '—'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Close Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => openCloseModal(pos)} 
                    disabled={processing === pos.id}
                    style={{ 
                      flex: 2,
                      padding: '10px', 
                      backgroundColor: COLORS.danger, 
                      border: 'none', 
                      borderRadius: '6px', 
                      color: '#fff', 
                      fontSize: '12px', 
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      opacity: processing === pos.id ? 0.5 : 1,
                    }}
                  >
                    <X size={14} />
                    Закрыть по рынку (${currentPrice.toFixed(2)})
                  </button>
                  {pos.take_profit && (
                    <button 
                      onClick={() => closePosition(pos.id, 'tp')} 
                      disabled={processing === pos.id} 
                      style={{ 
                        flex: 1,
                        padding: '10px', 
                        backgroundColor: `${COLORS.success}20`, 
                        border: `1px solid ${COLORS.success}`, 
                        borderRadius: '6px', 
                        color: COLORS.success, 
                        fontSize: '11px',
                        fontWeight: '600', 
                        cursor: 'pointer', 
                        opacity: processing === pos.id ? 0.5 : 1 
                      }}
                    >
                      TP ${pos.take_profit}
                    </button>
                  )}
                  {pos.stop_loss && (
                    <button 
                      onClick={() => closePosition(pos.id, 'sl')} 
                      disabled={processing === pos.id} 
                      style={{ 
                        flex: 1,
                        padding: '10px', 
                        backgroundColor: `${COLORS.danger}20`, 
                        border: `1px solid ${COLORS.danger}`, 
                        borderRadius: '6px', 
                        color: COLORS.danger, 
                        fontSize: '11px',
                        fontWeight: '600', 
                        cursor: 'pointer', 
                        opacity: processing === pos.id ? 0.5 : 1 
                      }}
                    >
                      SL ${pos.stop_loss}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Closed Positions */}
      {closedPositions.length > 0 && (
        <div>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
            Закрытые позиции
          </div>
          {closedPositions.map((pos, i) => (
            <div key={pos.id || i} style={{
              padding: '12px',
              backgroundColor: COLORS.background,
              borderRadius: '8px',
              marginBottom: '8px',
              opacity: 0.7,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: COLORS.text, fontWeight: '600' }}>{pos.symbol}</span>
                  <span style={{
                    color: pos.side === 'long' ? COLORS.success : COLORS.danger,
                    fontSize: '11px',
                  }}>
                    {pos.side?.toUpperCase()} {pos.leverage}x
                  </span>
                </div>
                <button
                  onClick={() => reopenPosition(pos.id)}
                  disabled={processing === pos.id}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: `${COLORS.success}15`,
                    border: 'none',
                    borderRadius: '4px',
                    color: COLORS.success,
                    fontSize: '11px',
                    cursor: processing === pos.id ? 'not-allowed' : 'pointer',
                    opacity: processing === pos.id ? 0.5 : 1,
                  }}
                >
                  ↺ Восстановить
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', fontSize: '12px' }}>
                <div><span style={{ color: COLORS.textSecondary }}>Вход:</span> <span style={{ color: COLORS.text }}>${pos.entry_price}</span></div>
                <div><span style={{ color: COLORS.textSecondary }}>Выход:</span> <span style={{ color: COLORS.text }}>${pos.close_price || '—'}</span></div>
                <div><span style={{ color: COLORS.textSecondary }}>Объём:</span> <span style={{ color: COLORS.text }}>{pos.quantity}</span></div>
                <div><span style={{ color: COLORS.textSecondary }}>PnL:</span> <span style={{ color: pos.pnl >= 0 ? COLORS.success : COLORS.danger }}>${pos.pnl?.toFixed(2)}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No positions */}
      {positions.length === 0 && (
        <div style={{ color: COLORS.textSecondary, fontSize: '13px', textAlign: 'center', padding: '40px' }}>
          <TrendingUp size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div>Нет позиций</div>
        </div>
      )}

      {/* Edit Position Modal */}
      {editingPosition && (
        <EditPositionModal
          position={editingPosition}
          clientId={clientId}
          onClose={() => setEditingPosition(null)}
          onSuccess={() => { setEditingPosition(null); onRefresh(); }}
        />
      )}

      {/* Close Position Modal */}
      {closingPosition && (
        <div 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            backgroundColor: 'rgba(0,0,0,0.8)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1100 
          }} 
          onClick={() => setClosingPosition(null)}
        >
          <div 
            style={{ 
              backgroundColor: COLORS.card, 
              borderRadius: '16px', 
              padding: '24px', 
              width: '100%', 
              maxWidth: '420px' 
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '10px', 
                  backgroundColor: `${COLORS.danger}20`, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <X size={20} color={COLORS.danger} />
                </div>
                <div>
                  <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700', margin: 0 }}>Закрыть позицию</h3>
                  <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{closingPosition.symbol} • {closingPosition.side?.toUpperCase()}</div>
                </div>
              </div>
              <button onClick={() => setClosingPosition(null)} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {/* Position Info */}
            <div style={{ padding: '12px', backgroundColor: COLORS.background, borderRadius: '10px', marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', textAlign: 'center' }}>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Цена входа</div>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>${closingPosition.entry_price}</div>
                </div>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Объём</div>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>{closingPosition.quantity}</div>
                </div>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Плечо</div>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>{closingPosition.leverage}x</div>
                </div>
              </div>
            </div>

            {/* Close Price */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Цена закрытия ($)</label>
                <button
                  onClick={() => {
                    const price = getMarketPrice(closingPosition.symbol);
                    setClosePrice(price.toString());
                    setClosePnl(calculatePnL(closingPosition, price).toFixed(2));
                  }}
                  style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: '11px' }}
                >
                  Рыночная: ${getMarketPrice(closingPosition.symbol).toFixed(2)}
                </button>
              </div>
              <input
                type="number"
                value={closePrice}
                onChange={(e) => handleClosePriceChange(e.target.value, closingPosition)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  color: COLORS.text,
                  fontSize: '16px',
                  fontWeight: '600',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* PnL Preview */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>PnL при закрытии ($)</label>
              <input
                type="number"
                value={closePnl}
                onChange={(e) => setClosePnl(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${parseFloat(closePnl) >= 0 ? COLORS.success : COLORS.danger}`,
                  borderRadius: '8px',
                  color: parseFloat(closePnl) >= 0 ? COLORS.success : COLORS.danger,
                  fontSize: '16px',
                  fontWeight: '700',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Warning */}
            <div style={{
              padding: '10px 12px',
              backgroundColor: `${COLORS.danger}10`,
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <AlertTriangle size={16} color={COLORS.danger} />
              <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                Позиция будет закрыта. Это действие необратимо.
              </span>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setClosingPosition(null)}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  color: COLORS.textSecondary,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => closePosition(closingPosition.id, 'manual', parseFloat(closePrice), parseFloat(closePnl))}
                disabled={processing === closingPosition.id || !closePrice}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: processing === closingPosition.id || !closePrice ? COLORS.border : COLORS.danger,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: processing === closingPosition.id || !closePrice ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <X size={16} />
                {processing === closingPosition.id ? 'Закрытие...' : 'Закрыть позицию'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionsTab;
